import { Box, ButtonBase, Collapse, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TimelineIcon from '@mui/icons-material/Timeline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import ShDateRangePicker from "../../../../components/theme/ShDateRangePicker";
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader";
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import { useTopbar } from "../../../../contexts/TopbarProvider";
import { useGetDaytotalsQuery } from "../../../../newapi/inbound/inboundsSlice";
import { useGetDayProgressionQuery, useGetDaytotalsAllQuery } from "../../../../newapi/reports/fulfillment/reportFulfillmentSlice";
import { setDayTotalsDateFilter } from "../../../../newapi/reports/fulfillment/state/daytotalsState";
import { ReactComponent as ArrowAlt } from '../../../../styles/svg/arrow_alt.svg';
import { tokens } from "../../../../theme";
import { formatNumber } from "../../../../utils/helpers";
import { BarChart, HorizontalBarChartContent, HorizontalBarChartContentReports } from "../../../../components/theme/charts/HorizontalBarChart";
import { DropdownBase } from "../../../../components/theme/dropdowns/Dropdown";
import Heading from "../../../../components/theme/text/Heading";
import TabViewBody from "../../../../components/theme/TabView3/TabViewBody";
import SHModal from "../../../../components/theme/Modal/SHModal";
import ApexCharts from 'apexcharts';
import ShDataGrid3 from "../../../../components/theme/datagrid3/ShDataGrid3";
import { useGetUserLogsQuery } from "../../../../newapi/global/logsSlice";
import { LogActionBadge } from "../../../../utils/logomapper";
import { generateColorFromTitle, generateIconColorFromTitle } from "../../../../components/theme/TabView3/IconColorGenerator";
import { getTranslatedDateAndTime } from "../../../../utils";
import { RELATION_ORDERS_DEFAULT } from "../../../../components/theme/datagrid3/groupingPresets/GroupingPresets";
import { GRANULARITY_KEYS } from "../../../../components/theme/datagrid3/groupingDateGranularities";
import { shDatagridRowHeightOptions } from "../../../../newapi/global/filter/DataGridGeneralOptions";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import ShLabelSkeleton from "../../../../components/theme/skeleton/ShLabelSkeleton";
import { OrdersBreakdownQuickAction } from "./components/DaytotalsKpiStrip";
import { TabContextProvider } from "../../../../contexts/TabContext";
import { GridFilterProvider } from "../../../../contexts/GridFilterContext";
// import { useGetRelationDashboardProductsQuery } from "../../../newapi/warehouse/productSlice";
const SpStockDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails"));
const SpUserDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpUserDetails/SpUserDetails"));
const SpOrderDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));
const SpInboundDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpInboundDetails/SpInboundDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpReturnDetails/SpReturnDetails"));
const SpPickingJobPickingList = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpPickingJobPickingList/SpPickingJobPickingList"));

const TopBarFilter = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    // const relationDashboardDates = useSelector((state) => state.relationDashboard);
    const daytotalsDates = useSelector((state) => state.dayTotalsReport);
    const dispatch = useDispatch();

    const saveDate = (value) => {
        dispatch(setDayTotalsDateFilter({
            startDate:value[0].formattedStartDate,
            // endDate:value[0].formattedEndDate,
        }))
    }

    return (
        <ShDateRangePicker 
            select={saveDate}
            explicit
            singleSelect
            startDate={daytotalsDates.startDate} 
            // endDate={relationDashboardDates.endDate} 
            darker 
            size={40}
            sx={{
                background: `${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                border:`2px solid ${"transparent"}`,
                "&:hover" : {
                    background: `${theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100]}`,
                    border:`2px solid ${colors.grey[200]}`
                },
                '@media screen and (max-width: 47.9375em)': {
                    height:32,
                    px:2,
                },
                '@media screen and (max-width: 34.375em)': {
                    display:'none',
                },
            }}
        />
    )
}


// Exported so a BI dashboard cell can embed the per-employee totals table. Self-fetching (reads the shared
// day-totals date from redux). Sidepanel drill-through is opt-in via the handlers; omit them (dashboard) for
// a read-only table. `sx` is merged into the root. DaytotalsUserRow is defined below — resolved at render.
export const DaytotalsEmployeeTable = ({ streamerMode, onLogRowClick = () => {}, onOpenUserLogs = () => {}, sx }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const daytotalsDates = useSelector((state) => state.dayTotalsReport);

    const sortTable = [
        { id: 0, name: "inbounds", rowsName: "inboundLines", pointsName: "inboundsPoints", column: t("inbounds") },
        { id: 1, name: "returns", rowsName: "returnLines", pointsName: "returnsPoints", column: t("returns") },
        { id: 2, name: "picked", rowsName: "pickedLines", pointsName: "pickedPoints", column: t("picked") },
        { id: 3, name: "packed", rowsName: "shipments", pointsName: "packedPoints", column: t("processed") }
    ];
    // The total only exists for points; units and rows of different activities do not add up
    const totalPointsSort = { id: 4, pointsName: "totalPoints", column: t("total") };
    const [sortedBy, setSortedBy] = useState(sortTable[2]);
    // Toggles the employee metrics between units (orders/inbounds/returns), rows (order-, inbound- and
    // returnlines, shipments) and the points of the scorecard
    const [view, setView] = useState("units");
    const sortOptions = view === "points" ? [...sortTable, totalPointsSort] : sortTable;

    const { data } = useGetDaytotalsAllQuery({ "date": daytotalsDates?.startDate }, { pollingInterval: 300000 });

    const changeView = (newView) => {
        if (newView === "points") {
            setSortedBy(totalPointsSort);
        } else if (sortedBy.id === totalPointsSort.id) {
            setSortedBy(sortTable[2]);
        }
        setView(newView);
    };

    const employeeTotals = useMemo(() => {
        if (!data?.employeeTotals) return [];
        const sortField = view === "points" ? sortedBy.pointsName : view === "rows" ? sortedBy.rowsName : sortedBy.name;
        return [...(data?.employeeTotals ?? [])].sort((a, b) => (b[sortField] ?? 0) - (a[sortField] ?? 0));
    }, [data?.employeeTotals, sortedBy, view]);

    return (
        <Box sx={{ ...sx }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: 'space-between',
                    alignItems: "center",
                    "@media screen and (max-width: 47.9375em)": {
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 4,
                        mt: 5,
                    }
                }}
            >
                <Subheading>{t("detailed")}</Subheading>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        "@media screen and (max-width: 47.9375em)": {
                            width: 1,
                            flexDirection: "column",
                            alignItems: "flex-start",
                        }
                    }}
                >
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                        height: 40,
                        px: 0.5,
                        borderRadius: 5,
                        display: "flex",
                        alignItems: 'center',
                        gap: 0.5,
                        "@media screen and (max-width: 47.9375em)": {
                            width: 1,
                        }
                    }}
                >
                    <ButtonBase
                        onClick={() => changeView("units")}
                        sx={{
                            height: 32,
                            px: 1.5,
                            borderRadius: 4,
                            background: view === "units" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent",
                            "@media screen and (max-width: 47.9375em)": {
                                flex: 1,
                            }
                        }}
                    >
                        <Text bold={view === "units"}>{t("units")}</Text>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => changeView("rows")}
                        sx={{
                            height: 32,
                            px: 1.5,
                            borderRadius: 4,
                            background: view === "rows" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent",
                            "@media screen and (max-width: 47.9375em)": {
                                flex: 1,
                            }
                        }}
                    >
                        <Text bold={view === "rows"}>{t("rows")}</Text>
                    </ButtonBase>
                    <ButtonBase
                        onClick={() => changeView("points")}
                        sx={{
                            height: 32,
                            px: 1.5,
                            borderRadius: 4,
                            background: view === "points" ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]) : "transparent",
                            "@media screen and (max-width: 47.9375em)": {
                                flex: 1,
                            }
                        }}
                    >
                        <Text bold={view === "points"}>{t("points")}</Text>
                    </ButtonBase>
                </Box>
                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                        height: 40,
                        px: 0.5,
                        borderRadius: 5,
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: 'center',
                        minWidth: 220,
                        "@media screen and (max-width: 47.9375em)": {
                            ml: "unset",
                            width: 1,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height: 32,
                            lineHeight: "32px",
                            px: 1.5,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            mr: 1.5,
                        }}
                    >
                        <Text>{t("sorted_by")}</Text>
                    </Box>
                    <Text sx={{ pr: 2 }} bold>{t(sortedBy.column)}</Text>

                    <DropdownBase
                        key={sortedBy}
                        displayName={"column"}
                        onChange={(selected) => {
                            setSortedBy(sortOptions[selected])
                        }}
                        useTranslation
                        options={sortOptions}
                        selected={sortedBy.id}
                        closeOnSelection
                        maxHeight={"unset"}
                        valueKey={"id"}
                        disallowDeselection
                    >
                        <ButtonBase
                            sx={{
                                ml: "auto",
                                width: 32,
                                height: 32,
                                background: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                color: "#fff",
                                borderRadius: 5,
                            }}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </ButtonBase>
                    </DropdownBase>
                </Box>
                </Box>
            </Box>
            {/* Points are stored every hour, so the view says how recent they are, or that no scorecard is filled in yet */}
            {view === "points" && data?.scorecard && (
                <Box sx={{ mt: 2 }}>
                    <Text light>
                        {!data.scorecard.active
                            ? t("scorecard_not_filled_in")
                            : data.scorecard.updatedAt
                                ? t("scorecard_points_updated_at", { time: format(new Date(data.scorecard.updatedAt), "HH:mm") })
                                : t("scorecard_points_not_calculated_yet")}
                    </Text>
                </Box>
            )}
            <Box
                sx={{
                    mt: 3,
                    display: "flex",
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                {employeeTotals.map((employee, idx) => (
                    <DaytotalsUserRow
                        key={idx}
                        idx={idx}
                        employee={employee}
                        view={view}
                        date={daytotalsDates?.startDate}
                        streamerMode={streamerMode}
                        onLogRowClick={onLogRowClick}
                        onOpenUserLogs={onOpenUserLogs}
                    />
                ))}
            </Box>
        </Box>
    );
};

const Daytotals = () => {
    
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { showExtraComponent, clearExtraComponent,showExtraTitle,clearExtraTitle } = useTopbar();

    
    const [productId, setProductId] = useState();
    const productIdRef = useRef(productId);
    const [streamerMode, setStreamerMode] = useState(false);

    const [productsSpIsExtended, setProductsSpIsExtended] = useState(false);
    const [spStockLoading, setSpStockLoading] = useState(false);
    const spStockLoadingRef = useRef(spStockLoading);

    const [userSpIsExtended, setUserSpIsExtended] = useState(false);
    const [spUserLoading, setSpUserLoading] = useState(false);
    const [spUserId, setSpUserId] = useState(null);

    // Log row sidepanel state
    const [logOrderId, setLogOrderId] = useState();
    const [logOrderSpIsExtended, setLogOrderSpIsExtended] = useState(false);
    const [spLogOrderLoading, setSpLogOrderLoading] = useState();

    const [logInboundId, setLogInboundId] = useState();
    const [logInboundSpIsExtended, setLogInboundSpIsExtended] = useState(false);
    const [spLogInboundLoading, setSpLogInboundLoading] = useState();

    const [logReturnId, setLogReturnId] = useState();
    const [logReturnSpIsExtended, setLogReturnSpIsExtended] = useState(false);
    const [spLogReturnLoading, setSpLogReturnLoading] = useState();

    const [logProductId, setLogProductId] = useState();
    const [logProductSpIsExtended, setLogProductSpIsExtended] = useState(false);
    const [spLogProductLoading, setSpLogProductLoading] = useState();

    const [logPickingJobId, setLogPickingJobId] = useState();
    const [logPickingJobSpIsExtended, setLogPickingJobSpIsExtended] = useState(false);
    const [spLogPickingJobLoading, setSpLogPickingJobLoading] = useState();

    const handleLogRowClick = (params) => {
        if (!params?.row) return;
        const row = params.row;

        if (row.order?.id) {
            setLogOrderId(row.order.id);
            if (!spLogOrderLoading) setSpLogOrderLoading(true);
            setLogOrderSpIsExtended(true);
        } else if (row.inbound?.id) {
            setLogInboundId(row.inbound.id);
            if (!spLogInboundLoading) setSpLogInboundLoading(true);
            setLogInboundSpIsExtended(true);
        } else if (row.return?.id) {
            setLogReturnId(row.return.id);
            if (!spLogReturnLoading) setSpLogReturnLoading(true);
            setLogReturnSpIsExtended(true);
        } else if (row.product?.id) {
            setLogProductId(row.product.id);
            if (!spLogProductLoading) setSpLogProductLoading(true);
            setLogProductSpIsExtended(true);
        } else if (row.pickingJob?.id) {
            setLogPickingJobId(row.pickingJob.id);
            if (!spLogPickingJobLoading) setSpLogPickingJobLoading(true);
            setLogPickingJobSpIsExtended(true);
        }
    };

    const daytotalsDates = useSelector((state) => state.dayTotalsReport);
    const [previousRange, setPreviousRange] = useState(null);
    // Open/picked counts only exist for the live day; a past date shows processed orders alone.
    const isToday = daytotalsDates.startDate === format(new Date(), "yyyy-MM-dd");

    //const {data: relationDashboardHeaderData ,isLoading, isFetching} = useGetRelationFullDashboardQuery(daytotalsDates);
    // const {data: relationDashboardBodyData ,isLoading:productsLoading, isFetching:productsFetching} = useGetRelationDashboardProductsQuery(daytotalsDates);
    const {data ,isLoading, isFetching} = useGetDaytotalsAllQuery({"date": daytotalsDates.startDate}, {
        pollingInterval: 300000,
    });
    const {data: inboundData ,isLoading:inboundLoading, isFetching:inboundFetching} = useGetDaytotalsQuery(undefined, {
        pollingInterval: 300000,
    });
   
const [totalInboundCount, setTotalInboundCount] = useState(0);
    useEffect(() => {
        if (inboundData && Array.isArray(inboundData)) {
            const total = inboundData.reduce((acc, item) => acc + (item.count || 0), 0);
            setTotalInboundCount(total);
        }
    }, [inboundData]);

    const [active,setActive] = useState(false);

    const timeFrames = [
        {
            id:0,
            value:"1h",
            column:t("per_hour"),
            barWidth:"28px",
            barRadius: 12,
        },
        {
            id:1,
            value:"30m",
            column:t("30m"),
            barWidth:"14px",
            barRadius: 9,
        },
        {
            id:2,
            value:"15m",
            column:t("15m"),
            barWidth:"14px",
            barRadius: 6,
        },
    ];
    const [activeTimeFrame,setActiveTimeFrame] = useState(timeFrames[0]);

    const {data: dayProgressionData ,isLoading: progressionLoading, isFetching: progressionFetching, isError: progressionError,refetch: refetchProgression} = useGetDayProgressionQuery({
        userId:-1,
        date:daytotalsDates.startDate,
        timeFrame:activeTimeFrame.value
    }, {
        pollingInterval: 300000,
    });

    
    const [open, setOpen] = useState(false);

    const dispatch = useDispatch();

    const saveDate = (value) => {
        dispatch(setDayTotalsDateFilter({
            startDate:value[0].formattedStartDate,
        }))
    }

    useEffect(() => {
        // Example: show different components based on user privileges
        showExtraComponent(<TopBarFilter/>);
  
        return () => {
            clearExtraComponent();
        };
    }, []);

    useEffect(() => {
        showExtraTitle(
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {t("daytotals")}
                <ButtonBase
                    onClick={() => setStreamerMode(prev => !prev)}
                    sx={{
                        ml:1.5,
                        height: 32,
                        width: 32,
                        borderRadius: 4,
                        background: streamerMode ? theme.palette.mode === "dark" ? colors.blue[100]: colors.primary[100] : (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]),
                        color: streamerMode ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] : colors.txt["secondary"],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "150ms",
                    }}
                >
                    {streamerMode ? <VideocamIcon fontSize="small" /> : <VideocamOffIcon fontSize="small" />}
                </ButtonBase>
            </Box>
        )

        return () => {
            clearExtraTitle();
        };
    }, [t,i18n.language,streamerMode,theme.palette.mode]) // eslint-disable-line react-hooks/exhaustive-deps
    
        
    const getColorForIndex = (index) => {
        if(index === 0) {
            return {
                iconBg: "#D7AC0E",
                iconColor:"#fff",
                background: "#FBF7E7",
                color:"#31415f",
                countColor: "#A7860B",
                countWeight: 1000,
            }
        } else if(index === 1) { 
            return {
                iconBg: "#8590A2",
                iconColor:"#fff",
                background: "#F3F4F6",
                color:"#31415f",
                countColor: "#31415F",
                countWeight: 1000,
            }
        }else if(index === 2) { 
            return {
                iconBg: "#D7840E",
                iconColor:"#fff",
                background: "#FBF3E7",
                color:"#31415f",
                countColor: "#D7840E",
                countWeight: 1000,
            }
        } else {
            
            return {
                iconBg: theme.palette.mode === "dark" ? colors.grey[800] : "#FFF",
                iconColor:theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
                background: theme.palette.mode === "dark" ? colors.grey[400] : "#FFF",
                color:theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
                countColor: theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
                countWeight: 400,
            }

        }
    }


    return (
        <Box 
            sx={{
                background:colors.bg['tertiary'], 
                width:1,
                paddingTop:9,
            }}
        >

            {!streamerMode && <Box
                sx={{
                    mt:2,
                    display:'flex',
                    justifyContent:"space-between",
                    alignItems:'center',
                    paddingX:4,
                    "@media screen and (max-width: 63.9735em)" :{
                        flexDirection:'column',
                        alignItems:'flex-start',
                        gap:0,
                    },
                    "@media screen and (max-width: 47.9735em)" :{
                        px:3,
                    },
                    '@media screen and (max-width: 34.375em)': {
                        mt:0,
                        gap:3,
                    },
                }}
            >
                <Box
                    sx={{
                        display:"none",
                        position:'fixed',
                        top:"72px",
                        width:1,
                        left:"0px",
                        px:2,
                        background:colors.bg["tertiary"],
                        zIndex:100,
                        pb:2,
                        "@media screen and (max-width: 47.9735em)" :{
                            display:"block"
                        },
                    }}
                >
                
                    <ShDateRangePicker 
                        select={saveDate}
                        explicit
                        singleSelect
                        startDate={daytotalsDates.startDate} 
                        // endDate={relationDashboardDates.endDate} 
                        darker 
                        size={40}
                        sx={{
                            width:1,
                            background: `${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                            border:`2px solid ${"transparent"}`,
                            "&:hover" : {
                                background: `${theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100]}`,
                                border:`2px solid ${colors.grey[200]}`
                            },
                            '@media screen and (max-width: 47.9375em)': {
                                height:32,
                                px:2,
                            },
                            '@media screen and (min-width: 34.375em)': {
                                display:'none',
                            },
                        }}

                        wrapperProps={{
                            '@media screen and (min-width: 34.375em)': {
                                display:'none',
                            },
                            width:1,
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        "@media screen and (max-width: 63.9735em)" :{
                            mb:4,
                        },
                        "@media screen and (max-width: 47.9735em)" :{
                            mt:7,
                        },
                        '@media screen and (max-width: 34.375em)': {
                            mb:0,
                        },

                    }}
                >
                    <OrdersBreakdownQuickAction breakdown={data?.orders?.breakdown?.processed}>
                    {(hoverProps) => (
                    <Box {...hoverProps} sx={{width:"fit-content"}}>
                    <Subheading
                        sx={{
                            fontWeight:"normal"
                        }}
                    >
                        {t("processed_orders")}
                    </Subheading>
                    <Box 
                        sx={{
                            mt:1,
                            display:'flex',
                            alignItems:"center",
                        }}
                    >
                        
                        <Typography
                            sx={{
                                lineHeight:"32px",
                                fontFamily:"Roboto, sans-serif",
                                fontSize:32,
                                fontWeight:"1000",
                                color:colors.txt["primary"]
                            }}
                        >
                            
                            {!isLoading && !isFetching && formatNumber(i18n, data?.orders?.processed)}
                        </Typography>

                    </Box>
                    </Box>
                    )}
                    </OrdersBreakdownQuickAction>
                    {/* {data?.orders?.open > 0 && data?.orders?.picked > 0 && (
                        
                    )} */}
                    {isToday && (
                    <Box
                        sx={{
                            height:40,

                            p:0.5,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            border:`1px solid ${colors.grey[100]}`,
                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                            borderRadius:5,
                            mt:1,
                        }}
                    >
                        <OrdersBreakdownQuickAction breakdown={data?.orders?.breakdown?.open}>
                        {(hoverProps) => (
                        <Box
                            {...hoverProps}
                            sx={{
                                px:0.5,
                                height:32,
                                display:"flex",
                                justifyContent:"flex-start",
                                alignItems:'center',
                            }}
                        >
                            <Box
                                sx={{
                                    height:24,
                                    width:24,
                                    display:"flex",
                                    justifyContent:"flex-start",
                                    alignItems:'center',
                                    background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    borderRadius:3,
                                    mr:1,
                                }}
                            >

                            </Box>
                            <Text sx={{mr:3}}>
                                {t("to_process_today")}
                            </Text>
                            <Text black>
                                {!isLoading && !isFetching && formatNumber(i18n, data?.orders?.open)}
                            </Text>
                        </Box>
                        )}
                        </OrdersBreakdownQuickAction>

                        {/* Picked */}
                        <OrdersBreakdownQuickAction breakdown={data?.orders?.breakdown?.picked}>
                        {(hoverProps) => (
                        <Box
                            {...hoverProps}
                            sx={{
                                ml:2,
                                px:0.5,
                                height:32,
                                display:"flex",
                                justifyContent:"flex-start",
                                alignItems:'center',
                                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                borderRadius:4,
                                pr:2,
                            }}
                        >
                            <Box
                                sx={{
                                    height:24,
                                    width:24,
                                    display:"flex",
                                    justifyContent:"flex-start",
                                    alignItems:'center',
                                    background:colors.green[100],
                                    borderRadius:3,
                                    mr:1,
                                }}
                            >

                            </Box>
                            <Text noBreak sx={{mr:3}}>
                                {t("of_which_picked")}
                            </Text>
                            <Text black>
                                {!isLoading && !isFetching && formatNumber(i18n, data?.orders?.picked)}
                            </Text>
                        </Box>
                        )}
                        </OrdersBreakdownQuickAction>
                    </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        minWidth:200,
                        display:"flex",
                        gap:2,
                        '@media screen and (max-width: 47.9375em)': {
                            width:1,
                            display: 'grid',          // Change to grid on smaller screens
                            gridTemplateColumns: 'repeat(2, 1fr)', // Two columns
                            gridTemplateRows: 'repeat(2, auto)',  // Two rows
                            gap: 2,                   // Maintain the gap between items
                        },
                        '@media screen and (max-width: 25em)': {
                            display:'flex',
                            flexDirection:'column',
                        },
                    }}
                >
                    {/* Returns */}
                    <Box
                        sx={{
                            py:1,
                            px:1,
                            display:"flex",
                            justifyContent:"center",
                            // alignItems:'center',
                            flexDirection:"column",
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            border:`1px solid ${colors.grey[100]}`,
                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                            borderRadius:5,
                            mt:1,
                        }}
                    >
                        <Text bold sx={{pl:1.5,pt:1,}}>{t("returns")}</Text>
                        <Box
                            sx={{
                                pt:1.5,
                                display:"flex",
                                gap:1,

                            }}
                        >
                            <Box
                                sx={{
                                    borderRadius:4,
                                    px:1.5,
                                    py:1.5,
                                    background:colors.green[100],
                                    display:"flex",
                                    flexDirection:"column",
                                    minWidth:100,
                                    "@media screen and (max-width:47.9375em)" : {
                                        flex:1,
                                    }

                                }}
                            >
                                <Text>{t("processed")}</Text>
                                <Text black variant="green">
                                    {!isLoading && !isFetching && formatNumber(i18n, data?.returns?.processed)}
                                </Text>
                            </Box>

                            <Box
                                sx={{
                                    borderRadius:4,
                                    px:1.5,
                                    py:1.5,
                                    background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    display:"flex",
                                    flexDirection:"column",
                                    minWidth:100,

                                }}
                            >
                                <Text>{t("announced")}</Text>
                                <Text black variant={theme.palette.mode === "dark" ? "blue" : "primary"}>

                                    {!isLoading && !isFetching && formatNumber(i18n, data?.returns?.received)}
                                </Text>
                            </Box>

                        </Box>
                    </Box>


                    {/* Inbounds */}
                    <Box
                        sx={{
                            py:1,
                            px:1,
                            display:"flex",
                            justifyContent:"center",
                            // alignItems:'center',
                            flexDirection:"column",
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            border:`1px solid ${colors.grey[100]}`,
                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                            borderRadius:5,
                            mt:1,
                            "@media screen and (max-width:47.9375em)" : {
                                mt:0,
                            }
                        }}
                    >
                        <Text bold sx={{pl:1.5,pt:1,}}>{t("inbounds")}</Text>
                        <Box
                            sx={{
                                pt:1.5,
                                display:"flex",
                                gap:1,

                            }}
                        >
                            <Box
                                sx={{
                                    borderRadius:4,
                                    px:1.5,
                                    py:1.5,
                                    background:colors.green[100],
                                    display:"flex",
                                    flexDirection:"column",
                                    minWidth:100,
                                    "@media screen and (max-width:47.9375em)" : {
                                        flex:1,
                                    }
                                }}
                            >
                                <Text>{t("processed")}</Text>
                                <Text black variant={"green"}>
                                    {!isLoading && !isFetching && formatNumber(i18n, data?.inbounds?.processed)}
                                    {/* {!isLoading && !isFetching && formatNumber(i18n, data.returns.received)} */}
                                </Text>
                            </Box>

                            <Box
                                sx={{
                                    borderRadius:4,
                                    px:1.5,
                                    py:1.5,
                                    background:colors.pink[100],
                                    display:"flex",
                                    flexDirection:"column",

                                }}
                            >
                                <Text>{t("awaiting")}</Text>
                                <Text black variant="pink">
                                    {!isLoading && !isFetching && formatNumber(i18n, data?.inbounds?.expected)}
                                </Text>
                            </Box>


                        </Box>
                    </Box>

                


                </Box>
            </Box>}

            <Box
                sx={{
                    mt: streamerMode ? 0 : 5,
                    width:1,
                    minHeight: streamerMode ? "calc(100% - 0px)" :  "calc(100% - 189.5px)",
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    position:"relative",
                    zIndex:2,

                    pt:4,
                    px:4,
                    display:"flex",
                    // alignItems:"flex-start",

                    "&:before" : {
                        content: "''",
                        display:"block",
                        width:"32px",
                        height:"32px",
                        position:"absolute",
                        left:0,
                        zIndex:1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        top:"0px",
                        height:"48px",
                        width:"24px",
                        borderTopLeftRadius:"24px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transition:"250ms"
                    },

                    "&:after" : {
                        content: "''",
                        display:"block",
                        width:"32px",
                        height:"32px",
                        position:"absolute",
                        top:0,
                        zIndex:1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        right:0,
                        height:"48px",
                        width:"24px",
                        borderTopLeftRadius:"24px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transform:"scaleX(-1)",
                        transition:"250ms"
                    },

                    "@media screen and (max-width: 47.9375em)" : {
                        flexDirection:"column",
                        "&:after, &:before" : {
                            top:"-0px"
                        }
                    }
                }}
            >
                {/* totalbox for inbounds, only on mobile */}
                <Box
                    onClick={() => setOpen(true)}
                    sx={{
                        py:2,
                        px:3,
                        display:"flex",
                        justifyContent:"center",
                        // alignItems:'center',
                        flexDirection:"column",
                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                        borderRadius:5,
                        mb:3,
                        mx:"-8px",
                        "@media screen and (min-width: 47.9376em)" : {
                            display:"none",

                        }
                    }}
                >
                    <Text>{t("expected_inbounds")}</Text>
                    <Heading>{totalInboundCount}</Heading>

                </Box>
                <SHModal
                    open={open}
                    onClose={() => setOpen(false)}
                    sx={{
                        display:"none",
                    }}
                >
                    <Box 
                        sx={{
                            textAlign:"center",
                            paddingY:3,
                            paddingX:4,
                            display:"flex",
                            flexDirection:"column",
                            gap:1
                        }}>
                        <Subheading>{t("expected_inbounds")}</Subheading>
                        {/* <Text light>The data presented will automatically be filtered through the selected warehouses</Text> */}
                    </Box>
                    <Box
                        sx={{
                            maxHeight:350,
                            background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            padding:3,
                            borderTopLeftRadius:16,
                            borderTopRightRadius:16,
                            display:"flex",
                            flexDirection:"column",
                            gap:1,
                            overflow:"auto"

                        }}
                    >
                        {inboundData && Array.from(inboundData, (e,idx) => {

                            return (
                                <Box
                                    key={e.relationId}
                                    sx={{
                                        display:"flex",
                                        justifyContent:"space-between",
                                        alignItems:'center',
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border:`1px solid ${colors.grey[100]}`,
                                        boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        borderRadius:4,
                                        py:1,
                                        pr:1,
                                        pl:2,
                                    }}
                                >
                                    {/* Header */}
                                
                                    <Text>{e.relationName}</Text>

                                    <Box
                                        sx={{
                                            height:32,
                                            width:32,
                                            borderRadius:2,
                                            background:colors.grey[100],
                                            display:"flex",
                                            justifyContent:"center",
                                            alignItems:'center',
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                            fontFamily:"Roboto, sans-serif",
                                            fontSize:17,
                                            fontWeight:"bold",
                                        }}
                                    >
                                        <Subheading>{e.count}</Subheading>
                                    </Box>

                                </Box>
                            )
                        })}
                    </Box>
                    
                </SHModal>

                <Box
                    sx={{
                        borderTopLeftRadius:"24px",
                        borderTopRightRadius:"24px",
                        flex:1,
                        p:3,
                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                        overflow:"hidden",
                        "@media screen and (max-width: 47.9375em)" : {
                            // background:"none"
                            mx:"-24px"
                        }
                        // height:1,
                    }}
                >
                    {!streamerMode && <Box
                        sx={{
                            flex:1,
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            px:2,
                            py:1.5,
                            borderRadius:4,
                            mb:4,
                            overflow:"hidden",
                            minWidth: 0,       // Prevent flex children from expanding layout
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                gap:4,
                                alignItems:'center',
                                "@media screen and (max-width: 47.9375em)" : {
                                    flexDirection:"column",
                                    alignItems:"flex-start",
                                    gap:2,
                                    py:1.5,
                                }
                                
                            }}
                        >
                            <Box>
                                <Subheading  bold>{t("processed_per")}</Subheading>
                            </Box>
                            <Box
                                sx={{
                                    ml:"auto",
                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                    height:40,
                                    px:0.5,
                                    borderRadius:5,
                                    display:"flex",
                                    justifyContent:"flex-start",
                                    alignItems:'center',
                                    minWidth:220,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        ml:"unset",
                                        width:1,
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        height:32,
                                        lineHeight:"32px",
                                        px:1.5,
                                        borderRadius:4,
                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                        mr:1.5,
                                    }}
                                >
                                    <Text>{t("timeframe")}</Text>
                                </Box>

                                <Text sx={{pr:2}} bold>{t(activeTimeFrame.column)}</Text>

                                <DropdownBase
                                    noPropagationOnSelect
                                    stopPropagation
                                    key={activeTimeFrame}
                                    displayName={"column"} 
                                    onChange={(selected) => {
                                        setActiveTimeFrame(timeFrames[selected])
                                    }}
                                    useTranslation
                                    options={timeFrames} // array
                                    selected={activeTimeFrame.id}
                                    closeOnSelection
                                    maxHeight={"unset"}
                                    valueKey={"id"}
                                    disallowDeselection
                                >
                                    <ButtonBase
                                        sx={{
                                            ml:"auto",
                                            width:32,
                                            height:32,
                                            background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                            color:"#fff",
                                            borderRadius:5,

                                        }}
                                    >
                                        <EditOutlinedIcon fontSize="small"/>
                                    </ButtonBase>
                                </DropdownBase>
                            </Box>
                        </Box>
                        {dayProgressionData &&
                            <HorizontalBarChartContentReports
                                hideFilter
                                width={activeTimeFrame.id === timeFrames[0].id ? (dayProgressionData?.picked?.categories.length * 112) : (dayProgressionData?.picked?.categories.length * 72)}
                                title={t("inbound")}
                                subtitle={t("last_7_days_and_today")}
                                type="bar"
                                staticColors={[
                                    colors.primary[400],
                                    colors.green[400],
                                    colors.orange[400],
                                ]}
                                isError={progressionError}
                                isLoading={progressionLoading || progressionFetching}
                                categories={dayProgressionData?.picked?.categories}
                                values={[
                                    ...(dayProgressionData?.picked?.series || []),
                                    ...(dayProgressionData?.shipped?.series || []),
                                    ...(dayProgressionData?.returns?.series || [])
                                ]}
                                // values={[dayProgressionData?.picked?.series,dayProgressionData?.shipped?.series] ?? []}
                                color={"default"}
                                onRetry={refetchProgression}
                                plotOptions={{
                                    bar: {
                                        borderRadius: activeTimeFrame.barRadius ?? 12,
                                        distributed:false,
                                        columnWidth:activeTimeFrame.barWidth ?? "28px",
                                        dataLabels: {
                                            position: "top", 
                                        },
                                    }
                                }}
                                sx={{
                                    box: {
                                        mt:1,
                                        mb:"-32px",
                                        overflowX:"scroll"
                                    }
                                }}
                            />
                        }

                    </Box>}

                    <DaytotalsEmployeeTable
                        streamerMode={streamerMode}
                        onLogRowClick={handleLogRowClick}
                        onOpenUserLogs={(userId) => {
                            setSpUserId(userId);
                            if (!spUserLoading) setSpUserLoading(true);
                            setUserSpIsExtended(true);
                        }}
                    />
                </Box>

                 <Box
                    sx={{
                        width:400,
                        pl:3,
                        "@media screen and (max-width: 1368px)" : {
                            width:350,
                        },
                        "@media screen and (max-width: 47.9375em)" : {
                            display:"none",
                        }
                    }}
                >
                    <Subheading>{t("expected_inbounds")}</Subheading>



                    <Box
                        sx={{
                            mt:2,
                            display:"flex",
                            flexDirection:'column',
                            gap:1.5,
                        }}
                    >
                        {inboundData && Array.from(inboundData, (e,idx) => {

                            return (
                                <Box
                                    key={e.relationId}
                                    sx={{
                                        display:"flex",
                                        justifyContent:"space-between",
                                        alignItems:'center',
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border:`1px solid ${colors.grey[100]}`,
                                        boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        borderRadius:4,
                                        py:1,
                                        pr:1,
                                        pl:2,
                                    }}
                                >
                                    {/* Header */}

                                    <Text>{e.relationName}</Text>

                                    <Box
                                        sx={{
                                            height:32,
                                            width:32,
                                            borderRadius:2,
                                            background:colors.grey[100],
                                            display:"flex",
                                            justifyContent:"center",
                                            alignItems:'center',
                                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                            fontFamily:"Roboto, sans-serif",
                                            fontSize:17,
                                            fontWeight:"bold",
                                        }}
                                    >
                                        <Subheading>{e.count}</Subheading>
                                    </Box>

                                </Box>
                            )
                        })}
                    </Box>
                </Box>


            </Box>


            {spStockLoading &&
                <SpStockDetails
                    isExtended={productsSpIsExtended}
                    setIsExtended={setProductsSpIsExtended}
                    productId={productId}
                />
            }
            {spUserLoading &&
                <SpUserDetails
                    isExtended={userSpIsExtended}
                    setIsExtended={setUserSpIsExtended}
                    userId={spUserId}
                    defaultTab={1}
                />
            }

            {spLogOrderLoading &&
                <SpOrderDetails
                    isExtended={logOrderSpIsExtended}
                    setIsExtended={setLogOrderSpIsExtended}
                    orderId={logOrderId}
                />
            }

            {spLogInboundLoading &&
                <SpInboundDetails
                    isExtended={logInboundSpIsExtended}
                    setIsExtended={setLogInboundSpIsExtended}
                    inboundId={logInboundId}
                />
            }

            {spLogReturnLoading &&
                <SpReturnDetails
                    isExtended={logReturnSpIsExtended}
                    setIsExtended={setLogReturnSpIsExtended}
                    returnId={logReturnId}
                />
            }

            {spLogProductLoading &&
                <SpStockDetails
                    isExtended={logProductSpIsExtended}
                    setIsExtended={setLogProductSpIsExtended}
                    productId={logProductId}
                />
            }

            {spLogPickingJobLoading &&
                <SpPickingJobPickingList
                    isExtended={logPickingJobSpIsExtended}
                    setIsExtended={setLogPickingJobSpIsExtended}
                    pickingJobListId={logPickingJobId}
                />
            }
        </Box>
    );
};

export default Daytotals;

const DaytotalsUserRow = (props) => {
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const outerRef = useRef();
    const  rippleRef = useRef();
    const [active,setActive] = useState(false);
    const [modalActive,setModalActive] = useState(false);

    const timeFrames = [
        {
            id:0,
            value:"1h",
            column:t("per_hour"),
            barWidth:"28px",
            barRadius: 12,
        },
        {
            id:1,
            value:"30m",
            column:t("30m"),
            barWidth:"21px",
            barRadius: 9,
        },
        {
            id:2,
            value:"15m",
            column:t("15m"),
            barWidth:"14px",
            barRadius: 6,
        },
    ];
    const [activeTimeFrame,setActiveTimeFrame] = useState(timeFrames[0]);

    const {data: dayProgressionData ,isLoading, isFetching, isError,refetch} = useGetDayProgressionQuery({
        userId:props.employee.id,
        date:props.date,
        timeFrame:activeTimeFrame.value
    },{skip:!active && !modalActive});


    const getPercentageClass = (percent) => {
        if(percent < 1) {
            return "negative";
        } else {
            return "positive";
        }
    }

    const getPercentage = (percent) => {
        if(percent < 1) {
            return (1 - percent) * 100;
        } else {
            return percent * 100 - 100;
        }
    }

    const getColorForIndex = (index) => {
        if(index === 0) {
            return {
                iconBg: "#D7AC0E",
                iconColor:"#fff",
                background: "#FBF7E7",
                color:"#31415f",
                countColor: "#A7860B",
                countWeight: 1000,
            }
        } else if(index === 1) { 
            return {
                iconBg: "#8590A2",
                iconColor:"#fff",
                background: "#F3F4F6",
                color:"#31415f",
                countColor: "#31415F",
                countWeight: 1000,
            }
        }else if(index === 2) { 
            return {
                iconBg: "#D7840E",
                iconColor:"#fff",
                background: "#FBF3E7",
                color:"#31415f",
                countColor: "#D7840E",
                countWeight: 1000,
            }
        } else {
            return {
                iconBg: theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[100],
                iconColor:theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                color:theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
                countColor: theme.palette.mode === "dark" ? colors.txt["light"] : colors.txt["primary"],
                countWeight: 400,
            }

        }
    }

    const [colorMap, setColorMap] = useState(getColorForIndex(props.idx))

    // Names of the parts the points of a column consist of
    const pointPartLabels = {
        pick_locations: t("scorecard_part_pick_locations"),
        pick_extra_products: t("scorecard_part_pick_extra_products"),
        pick_extra_pieces: t("scorecard_part_pick_extra_pieces"),
        ship_orders: t("scorecard_part_ship_orders"),
        ship_extra_lines: t("scorecard_part_ship_extra_lines"),
        return_returns: t("scorecard_part_return_returns"),
        return_extra_lines: t("scorecard_part_return_extra_lines"),
        receive_lines: t("scorecard_part_receive_lines"),
        receive_repeats: t("scorecard_part_receive_repeats"),
        putaway_locations: t("scorecard_part_putaway_locations"),
        putaway_extra_products: t("scorecard_part_putaway_extra_products"),
    };

    // Shown when hovering the points of a column: what they consist of, e.g. 145 orders and 14 extra
    // orderlines. Empty outside the points view and for a column without points, so nothing pops up there.
    const getPointsBreakdown = (column) => {
        const parts = props.view === "points" ? (props.employee.pointsBreakdown?.[column] ?? []) : [];
        if (parts.length === 0) return "";

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
                {parts.map((part) => (
                    <Box key={part.part} sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
                        <span>{pointPartLabels[part.part] ?? part.part}: {formatNumber(i18n, part.count)}</span>
                        <span>{formatNumber(i18n, part.points)} {t("points").toLowerCase()}</span>
                    </Box>
                ))}
            </Box>
        );
    };

    // Points have no monthly average to compare against, so the percentage badges are left out for them
    const showPoints = props.view === "points";

    // Values shown for this employee: units (inbounds/returns/orders), rows (inbound-, return- and orderlines,
    // shipments) or the points of the scorecard
    const displayedTotals = showPoints ? {
        inbounds: props.employee.inboundsPoints ?? 0,
        returns: props.employee.returnsPoints ?? 0,
        picked: props.employee.pickedPoints ?? 0,
        packed: props.employee.packedPoints ?? 0,
        pickedPercent: 0,
        packedPercent: 0,
    } : props.view === "rows" ? {
        inbounds: props.employee.inboundLines ?? 0,
        returns: props.employee.returnLines ?? 0,
        picked: props.employee.pickedLines ?? 0,
        packed: props.employee.shipments ?? 0,
        pickedPercent: props.employee.pickedLinesPercent ?? 0,
        packedPercent: props.employee.shipmentsPercent ?? 0,
    } : {
        inbounds: props.employee.inbounds,
        returns: props.employee.returns,
        picked: props.employee.picked,
        packed: props.employee.packed,
        pickedPercent: props.employee.pickedPercent,
        packedPercent: props.employee.packedPercent,
    };


    return (
        <>
        <ButtonBase
            // disableRipple
            onClick={(e) => {
                if (props.streamerMode) return;
                const innerButton = outerRef.current.querySelector('.user-log-feed');
                if (innerButton && innerButton.contains(e.target)) {
                    return;
                }
                const logsGrid = outerRef.current.querySelector('.daytotals-user-logs');
                if (logsGrid && logsGrid.contains(e.target)) {
                    return;
                }
                if(window.innerWidth > 767){
                    setActive(active => !active);
                } else {
                    setModalActive(active => !active);
                }
            }}
            ref={outerRef}
            component="div"
            key={props.employee.id}
            sx={{
                cursor: props.streamerMode ? "default" : "pointer",
                position:'relative',
                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                background:colorMap.background,

                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                // border:`1px solid ${colors.grey[100]}`,
                // boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                p:2,
                borderRadius:4,
                display:"flex",
                flexDirection:"column"
            }}
        >
            
            {/* Header */}
            <Box
                sx={{
                    width:1,
                    
                    display:"flex",
                    justifyContent:"flex-start",
                    alignItems:'center',
                    "@media screen and (max-width: 47.9375em)" : {
                        flexWrap:"wrap"
                    }

                }}
            >
                <Box
                    sx={{
                        height:32,
                        width:32,
                        borderRadius:4,
                        // background:colors.orange[400],
                        background:colorMap.iconBg,
                        display:"flex",
                        justifyContent:"center",
                        alignItems:'center',
                        // color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                        color:colorMap.iconColor,
                        fontFamily:"Roboto, sans-serif",
                        fontSize:17,
                        fontWeight:"bold",
                    }}
                >
                    {props.idx+1}
                </Box>
                <Subheading 
                    color={colorMap.color} 
                    sx={{
                        ml:1.5,

                        "@media screen and (max-width: 47.9375em)" : {
                            width:0.5,
                        }
                    }}
                >{props.employee.name}</Subheading>

                
                <Box
                    sx={{
                        ml:"auto",
                        px:0.5,
                        height:32,
                        display:"flex",
                        justifyContent:"flex-start",
                        alignItems:'center',
                        
                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius:4,
                        pr:2,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:"calc(50% - 8px)",
                            ml:0,
                            mt:2,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height:24,
                            px:1,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            background:colors.yellow[100],
                            borderRadius:3,
                            mr:1,
                        }}
                    >
                        <Text>{t("inbound")}</Text>
                    </Box>
                    {!props.streamerMode && <Tooltip arrow title={getPointsBreakdown("inbounds")}><Box component="span"><Text black>
                        {formatNumber(i18n, displayedTotals.inbounds)}
                    </Text></Box></Tooltip>}
                </Box>

                <Box
                    sx={{
                        ml:2,
                        px:0.5,
                        height:32,
                        display:"flex",
                        justifyContent:"flex-start",
                        alignItems:'center',

                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius:4,
                        pr:2,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:"calc(50% - 8px)",
                            ml:2,
                            mt:2,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height:24,
                            px:1,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            background:colors.orange[100],
                            borderRadius:3,
                            mr:1,
                        }}
                    >
                        <Text>{t("return")}</Text>
                    </Box>
                    {!props.streamerMode && <Tooltip arrow title={getPointsBreakdown("returns")}><Box component="span"><Text black>
                        {formatNumber(i18n, displayedTotals.returns)}
                    </Text></Box></Tooltip>}
                </Box>

                <Box
                    sx={{
                        ml:2,
                        px:0.5,
                        height:32,
                        display:"flex",
                        justifyContent:"flex-start",
                        alignItems:'center',
                        
                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius:4,
                        width:200,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:1,
                            ml:0,
                            mt:1.5,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height:24,
                            px:1,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                            borderRadius:3,
                            mr:1,
                        }}
                    >
                        <Text>{t("picked")}</Text>
                    </Box>
                    {!props.streamerMode && <Tooltip arrow title={getPointsBreakdown("picked")}><Box component="span"><Text black>
                        {formatNumber(i18n, displayedTotals.picked)}
                    </Text></Box></Tooltip>}

                    <Box
                        className={getPercentageClass(displayedTotals.pickedPercent)}
                        sx={{
                            display: showPoints ? "none" : "flex",
                            alignItems: "center",
                            ml:2,
                            ml:"auto",
                            color: colors.txt["primary"],
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 14,
                            fontWeight:"700",
                            borderRadius:3,
                            px:1,
                            height:24,
                            "& .arrow": {
                                mr:1,
                                width: 12,
                                "& *": {
                                    transition: "250ms",
                                    // fill: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                },
                            },

                            "&.positive" : {
                                background:colors.green[100],
                                // color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                color:colors.green[400],
                                fontWeight:"1000",
                                "& .arrow": {
                                    display:'block',
                                    width: 12,
                                    transform: "rotate(-90deg)",
                                    "& *": {
                                        // transition: "250ms",
                                        fill:colors.green[400],
                                        // fill: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                    },
                                },
                            },

                            "&.negative" : {
                                background:colors.red[100],
                                // color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                color:colors.red[400],
                                fontWeight:"1000",
                                "& .arrow": {
                                    display:'block',
                                    width: 12,
                                    transform: "rotate(90deg)",
                                    "& *": {
                                        // transition: "250ms",
                                        fill:colors.red[400],
                                    },
                                },
                            },
                        }}
                    >
                        <ArrowAlt className="arrow" />

                        {formatNumber(i18n, getPercentage(displayedTotals.pickedPercent))}%

                    </Box>
                </Box>

                <Box
                    sx={{
                        ml:2,
                        px:0.5,
                        height:32,
                        display:"flex",
                        justifyContent:"flex-start",
                        alignItems:'center',
                        
                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius:4,
                        width:220,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:1,
                            ml:0,
                            mt:1.5,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height:24,
                            px:1,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            background:colors.green[100],
                            borderRadius:3,
                            mr:1,
                        }}
                    >
                        <Text>{t("processed")}</Text>
                    </Box>
                    {!props.streamerMode && <Tooltip arrow title={getPointsBreakdown("packed")}><Box component="span"><Text black>
                        {formatNumber(i18n, displayedTotals.packed)}
                    </Text></Box></Tooltip>}

                    <Box
                        className={getPercentageClass(displayedTotals.packedPercent)}
                        sx={{
                            display: showPoints ? "none" : "flex",
                            alignItems: "center",
                            ml:2,
                            ml:"auto",
                            color: colors.txt["primary"],
                            fontFamily: "Roboto, sans-serif",
                            fontSize: 14,
                            fontWeight:"700",
                            borderRadius:3,
                            px:1,
                            height:24,
                            "& .arrow": {
                                mr:1,
                                width: 12,
                                "& *": {
                                    transition: "250ms",
                                    // fill: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                },
                            },

                            "&.positive" : {
                                background:colors.green[100],
                                // color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                color:colors.green[400],
                                fontWeight:"1000",
                                "& .arrow": {
                                    display:'block',
                                    width: 12,
                                    transform: "rotate(-90deg)",
                                    "& *": {
                                        // transition: "250ms",
                                        fill:colors.green[400],
                                        // fill: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                    },
                                },
                            },

                            "&.negative" : {
                                background:colors.red[100],
                                // color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                color:colors.red[400],
                                fontWeight:"1000",
                                "& .arrow": {
                                    display:'block',
                                    width: 12,
                                    transform: "rotate(90deg)",
                                    "& *": {
                                        // transition: "250ms",
                                        fill:colors.red[400],
                                    },
                                },
                            },
                        }}
                    >
                        <ArrowAlt className="arrow" />
                        {formatNumber(i18n, getPercentage(displayedTotals.packedPercent))}%

                    </Box>
                </Box>
                {showPoints && (
                <Box
                    sx={{
                        ml:2,
                        px:0.5,
                        height:32,
                        display:"flex",
                        justifyContent:"flex-start",
                        alignItems:'center',

                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                        borderRadius:4,
                        pr:2,
                        "@media screen and (max-width: 47.9375em)" : {
                            width:1,
                            ml:0,
                            mt:1.5,
                        }
                    }}
                >
                    <Box
                        sx={{
                            height:24,
                            px:1,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            borderRadius:3,
                            mr:1,
                        }}
                    >
                        <Text>{t("total")}</Text>
                    </Box>
                    {!props.streamerMode && <Text black>
                        {formatNumber(i18n, props.employee.totalPoints ?? 0)}
                    </Text>}
                </Box>
                )}
                {!props.streamerMode &&
                <ButtonBase
                    className="user-log-feed"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        if (props.onOpenUserLogs) {
                            props.onOpenUserLogs(props.employee.id);
                        }
                    }}
                    sx={{
                        height:32,
                        width:32,
                        background: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[300],
                        display:"flex",
                        alignItems:"center",
                        justifyContent:'center',
                        borderRadius:4,
                        ml:2,
                        color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["light"]
                    }}
                >
                    <TimelineIcon/>

                </ButtonBase>
                }
            </Box>

            <Collapse
                in={active}
                sx={{
                    width:1,
                }}
            >
                <Box
                    sx={{
                        mt:2,
                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                        borderRadius:3,
                        p:1.5,
                        display:"flex",
                        flexDirection:"column",
                        gap:1.5,
                    }}
                >
                    <Box
                        sx={{
                            flex:1,
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            px:2,
                            py:1.5,
                            borderRadius:2,
                            overflow:"hidden",
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                gap:4,

                                "@media screen and (max-width: 47.9375em)" : {
                                    flexWrap:"wrap"
                                }
                            }}
                        >
                            <Box>
                                <Text  bold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{t("picked")}</Text>
                                <Box>
                                    <Subheading>
                                        {formatNumber(i18n, displayedTotals.picked)}
                                    </Subheading>
                                </Box>
                            </Box>
                            <Box>
                                <Text bold variant="green">{t("processed")}</Text>
                                <Box>
                                    <Subheading>
                                        {formatNumber(i18n, displayedTotals.packed)}
                                    </Subheading>
                                </Box>
                            </Box>
                            <Box>
                                <Text bold variant="orange">{t("returns")}</Text>
                                <Box>
                                    <Subheading>
                                        {formatNumber(i18n, displayedTotals.returns)}
                                    </Subheading>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    ml:"auto",
                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                    height:40,
                                    px:0.5,
                                    borderRadius:5,
                                    display:"flex",
                                    justifyContent:"flex-start",
                                    alignItems:'center',
                                    minWidth:220,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        width:1,
                                        mt:"-16px"
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        height:32,
                                        lineHeight:"32px",
                                        px:1.5,
                                        borderRadius:4,
                                        background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                        mr:1.5,
                                    }}
                                >
                                    <Text>{t("timeframe")}</Text>
                                </Box>

                                <Text sx={{pr:2}} bold>{t(activeTimeFrame.column)}</Text>

                                <DropdownBase
                                    noPropagationOnSelect
                                    stopPropagation
                                    key={activeTimeFrame}
                                    displayName={"column"} 
                                    onChange={(selected) => {
                                        setActiveTimeFrame(timeFrames[selected])
                                    }}
                                    useTranslation
                                    options={timeFrames} // array
                                    selected={activeTimeFrame.id}
                                    closeOnSelection
                                    maxHeight={"unset"}
                                    valueKey={"id"}
                                    disallowDeselection
                                >
                                    <ButtonBase
                                        sx={{
                                            ml:"auto",
                                            width:32,
                                            height:32,
                                            background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                            color:"#fff",
                                            borderRadius:5,

                                        }}
                                    >
                                        <EditOutlinedIcon fontSize="small"/>
                                    </ButtonBase>
                                </DropdownBase>
                            </Box>
                        </Box>
                        {dayProgressionData &&
                            <HorizontalBarChartContent
                                hideFilter
                                width={activeTimeFrame.id === timeFrames[0].id ? (dayProgressionData?.picked?.categories.length * 112) : (dayProgressionData?.picked?.categories.length * 72)}
                                title={t("inbound")}
                                subtitle={t("last_7_days_and_today")}
                                type="bar"
                                staticColors={[
                                    colors.primary[400],
                                    colors.green[400],
                                    colors.orange[400],
                                ]}
                                isError={isError}
                                isLoading={isLoading || isFetching}
                                categories={dayProgressionData?.picked?.categories}
                                values={[
                                    ...(dayProgressionData?.picked?.series || []),
                                    ...(dayProgressionData?.shipped?.series || []),
                                    ...(dayProgressionData?.returns?.series || [])
                                ]}
                                // values={[dayProgressionData?.picked?.series,dayProgressionData?.shipped?.series] ?? []}
                                color={"default"}
                                onRetry={refetch}
                                plotOptions={{
                                    bar: {
                                        borderRadius: activeTimeFrame.barRadius ?? 12,
                                        distributed:false,
                                        columnWidth:activeTimeFrame.barWidth ?? "28px",
                                        dataLabels: {
                                            position: "top", 
                                        },
                                    }
                                }}
                                sx={{
                                    box: {
                                        mt:1,
                                        mb:"-32px",
                                        overflowX:"scroll"
                                    }
                                }}
                            />
                        }
                        <Box className="daytotals-user-logs">
                            <DaytotalsUserLogs
                                employeeId={props.employee.id}
                                date={props.date}
                                active={active}
                                onLogRowClick={props.onLogRowClick}
                            />
                        </Box>

                    </Box>

                </Box>
            </Collapse>
           

        </ButtonBase>
        <SHModal
            key={dayProgressionData}
            open={modalActive}
            onClose={(e) => {
                e.stopPropagation();
                setModalActive(false);
            }}
        >
            <Box
                sx={{
                    py:4,
                    px:3,
                }}
            >
                <Subheading sx={{textAlign:"center",pb:2,}}>{props.employee.name}</Subheading>
                <Box
                    sx={{
                        display:"flex",
                        gap:2,

                        "@media screen and (max-width: 47.9375em)" : {
                            flexWrap:"wrap",
                            justifyContent:'center',
                        }
                    }}
                >
                    <Box sx={{
                        p:1.5,
                        background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                        borderRadius:4,
                    }}>
                        <Text  bold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{t("picked")}</Text>
                        <Subheading>
                            {formatNumber(i18n, displayedTotals.picked)}
                        </Subheading>
                    </Box>
                    <Box sx={{
                        p:1.5,
                        background:colors.green[100],
                        borderRadius:4,
                    }}>
                        <Text bold variant="green">{t("processed")}</Text>
                        <Subheading>
                            {formatNumber(i18n, displayedTotals.packed)}
                        </Subheading>
                    </Box>
                    <Box sx={{
                        p:1.5,
                        background:colors.orange[100],
                        borderRadius:4,
                    }}>
                        <Text bold variant="orange">{t("returns")}</Text>
                        <Subheading>
                            {formatNumber(i18n, displayedTotals.returns)}
                        </Subheading>
                    </Box>

                </Box>
            </Box>
            <Box
                sx={{
                    // maxHeight:350,
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    px:3,
                    py:1,
                    pb:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    minHeight:270,
                    // display:"flex",
                    // flexDirection:"column",
                    // gap:1,
                    // overflow:"auto"

                }}
            >

                    <Box
                        sx={{
                            ml:"auto",
                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                            border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                            boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                            height:40,
                            px:0.5,
                            borderRadius:5,
                            display:"flex",
                            justifyContent:"flex-start",
                            alignItems:'center',
                            minWidth:220,
                            "@media screen and (max-width: 47.9375em)" : {
                                width:1,
                                mt:2,
                                // mx:3,
                            }
                        }}
                    >
                        <Box
                            sx={{
                                height:32,
                                lineHeight:"32px",
                                px:1.5,
                                borderRadius:4,
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                mr:1.5,
                            }}
                        >
                            <Text>{t("timeframe")}</Text>
                        </Box>

                        <Text sx={{pr:2}} bold>{t(activeTimeFrame.column)}</Text>

                        <DropdownBase
                            modalClose={(e) => {
                                console.log("owo");
                                console.log(e);
                            }}
                            noPropagationOnSelect
                            stopPropagation
                            key={activeTimeFrame}
                            displayName={"column"} 
                            onChange={(selected) => {
                                setActiveTimeFrame(timeFrames[selected])
                            }}
                            useTranslation
                            options={timeFrames} // array
                            selected={activeTimeFrame.id}
                            closeOnSelection
                            maxHeight={"unset"}
                            valueKey={"id"}
                            disallowDeselection
                            modalPropagation
                        >
                            <ButtonBase
                                sx={{
                                    ml:"auto",
                                    width:32,
                                    height:32,
                                    background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                    color:"#fff",
                                    borderRadius:5,

                                }}
                            >
                                <EditOutlinedIcon fontSize="small"/>
                            </ButtonBase>
                        </DropdownBase>
                    </Box>

                {dayProgressionData &&
                    <HorizontalBarChartContent
                        id={"hbcc"+props.employee.id}
                        hideFilter
                        height={"100%"}
                        isModal
                        width={activeTimeFrame.id === timeFrames[0].id ? (dayProgressionData?.picked?.categories.length * 112) : (dayProgressionData?.picked?.categories.length * 72)}
                        type="bar"
                        staticColors={[
                            colors.primary[400],
                            colors.green[400],
                            colors.orange[400],
                        ]}
                        isError={isError}
                        isLoading={isLoading || isFetching}
                        categories={dayProgressionData?.picked?.categories}
                        values={[
                            ...(dayProgressionData?.picked?.series || []),
                            ...(dayProgressionData?.shipped?.series || []),
                            ...(dayProgressionData?.returns?.series || [])
                        ]}
                        // values={[dayProgressionData?.picked?.series,dayProgressionData?.shipped?.series] ?? []}
                        color={"default"}
                        onRetry={refetch}
                        plotOptions={{
                            bar: {
                                borderRadius: activeTimeFrame.barRadius ?? 12,
                                distributed:false,
                                columnWidth:activeTimeFrame.barWidth ?? "28px",
                                dataLabels: {
                                    position: "top", 
                                },
                            }
                        }}
                        sx={{
                            box: {
                                mt:1,
                                mb:"-32px",
                                overflowX:"scroll"
                            }
                        }}
                    />
                }
            </Box>
        </SHModal>
     </>

    )
}

const LogTypeLabel = ({ params, type }) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const bg = generateColorFromTitle(type, theme.palette.mode, 93);
    const color = generateIconColorFromTitle(type, theme.palette.mode);

    const row = params.row;
    const ref = row.product?.sku
        || row.order?.barcode || row.order?.webshopreference
        || row.inbound?.barcode || row.inbound?.reference
        || row.pickingJob?.barcode
        || row.return?.reference
        || null;

    return (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent:"space-between",
                height: 32,
                px: 2,
                borderRadius: 4,
                background: bg,
                color: color,
                fontSize: 14,
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
            }}
        >
            {t(type)}
            <b>
                {ref}
            </b>
        </Box>
    );
};

const logGridOptions = {
    hide: true,
    disableCustomActionsOnPhone: true,
};

const DaytotalsUserLogs = ({ employeeId, date, active, onLogRowClick }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const config = useMemo(() => ({
        userId: employeeId,
        targetDate: date,
    }), [employeeId, date]);

    const columns = useMemo(() => [
        {
            field: "type",
            headerName: t("type"),
            width:230,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShLabelSkeleton />;
                if (!params.value) return null;
                return <LogTypeLabel params={params} type={params.value} />;
            },
        },
        {
            field: "description",
            headerName: t("description"),
            flex: 3,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                    const row = params.row; 
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <LogActionBadge action={row.action} mode={theme.palette.mode} />
                        <Text noBreak>{params.value}</Text>
                    </Box>
                );
            },
        },
        {
            field: "createdAt",
            headerName: t("created_on"),
            flex: 1,
            grouping: RELATION_ORDERS_DEFAULT.CREATED_AT,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                const cDateTime = getTranslatedDateAndTime(params.value, i18n);
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Text semibold>{cDateTime.time}</Text>
                        <Text light>{cDateTime.date}</Text>
                    </Box>
                );
            },
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: () => null,
        },
    ], [t, i18n, theme.palette.mode]);

    if (!active) return null;

    return (
        <TabContextProvider>
            <GridFilterProvider activeChild={0}>
                <Box
                    sx={{
                        // background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                        // border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                        // boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                        // borderRadius: 2,
                        overflow: "hidden",
                        height:600,
                        "@media screen and (max-width: 47.9375em)" : {
                            display:'none',
                        }
                    }}
                >
                    <ShDataGrid3
                        inline
                        key={theme.palette.mode}
                        gridKey={`daytotals.userLogs.${employeeId}`}
                        content={useGetUserLogsQuery}
                        config={config}
                        gridOptions={logGridOptions}
                        infiniteScroll
                        enableGrouping
                        defaultGroupBy={{ field: "createdAt", granularity: GRANULARITY_KEYS.MIN_15 }}
                        defaultRowHeight={shDatagridRowHeightOptions[0].value}
                        columns={columns}
                        sortModel={{ field: "createdAt", type: "DESC" }}
                        onRowClick={onLogRowClick}
                    />
                </Box>
            </GridFilterProvider>
        </TabContextProvider>
    );
};